import * as React from "react";
import {
  Form,
  FormInputGroup,
  FormInputGroupLayout,
  FormInterface,
  FormValues,
  StatesUpdateCallback,
  ValidationState,
} from "../Input/Form";
import { InputFormGroup } from "./InputFormTypes";

export interface InputFormComponent {
  /**
   * Method to retrieve the form values
   */
  getFormValues: () => FormValues | undefined;
  /**
   * Method when validation is enabled on the InputForm
   */
  enableValidation: () => void;
  /**
   * Method when validation is enabled or disabled
   */
  isValidationEnabled: () => boolean;
  /**
   * Method for the submit action on the InputForm
   */
  submit: () => void;
  /**
   * Method to get form state on the InputForm
   */
  getFormState: () => ValidationState;
  /**
   * Method to check if form has validation
   */
  hasValidation: () => boolean;
}

export interface InputFormProps {
  /**
   * Component reference for the InputForm
   */
  componentRef?: (formComponent: InputFormComponent) => void;
  /**
   * Layout for the InputForm component
   */
  layout?: FormInputGroupLayout;
  /**
   * Callback when the state is updated on the InputForm component
   */
  onStatesUpdate?: StatesUpdateCallback;
  /**
   * Callback when the form values are submitted on the InputForm component
   */
  onSubmit: (formValues: FormValues) => void;
  /**
   * Components to be displayed on the InputForm
   */
  children: React.ReactNode;

  /**
   * Callback when form field value(s) changes
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onFormChange?: (formValues: FormValues, fieldValue: any, field: string, group?: string) => void;
}

/**
 * The InputForm component renders the components provided by the caller
 * within a form in the specified layout
 */
export const InputForm = ({
  componentRef,
  layout = FormInputGroupLayout.WIDE,
  onStatesUpdate,
  onSubmit,
  children,
  onFormChange,
}: InputFormProps): JSX.Element => {
  const formRef = React.useRef<FormInterface>();
  const setFormRef = (form: FormInterface): void => {
    formRef.current = form;
  };

  const enableValidation = (): void => {
    formRef.current?.enableValidation();
  };

  const isValidationEnabled = (): boolean => formRef.current?.isValidationEnabled() || false;

  const submit = (): void => {
    formRef.current?.submit();
  };

  const getFormState = (): ValidationState => formRef.current?.getFormState() as ValidationState;

  const hasValidation = (): boolean => formRef.current?.hasValidation() as boolean;

  const getFormValues = (): FormValues | undefined => formRef.current?.getFormValues();

  React.useEffect(() => {
    if (componentRef) {
      componentRef({
        enableValidation,
        isValidationEnabled,
        submit,
        getFormState,
        getFormValues,
        hasValidation,
      } as InputFormComponent);
    }
  // Only at mount time. Others props should not have side effect beyond their initial values
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Form
      componentRef={setFormRef}
      onSubmit={onSubmit}
      onStatesUpdate={onStatesUpdate}
      onValueUpdate={onFormChange}
    >
      <FormInputGroup
        groupName={InputFormGroup}
        layout={layout}
        // style={{ paddingLeft: "8px", maxWidth: "700px" }}
      >
        {children}
      </FormInputGroup>
    </Form>
  );
};
